import { Copy, GripVertical, Minus, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, errMsg, imgUrl, money } from "@/lib/api";

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: "relative",
    zIndex: isDragging ? 10 : undefined,
    background: isDragging ? "hsl(var(--card))" : undefined,
  };
  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`product-row-${id}`}>
      <TableCell className="w-8 pr-0">
        <button type="button" className="cursor-grab touch-none text-muted-foreground hover:text-foreground p-1" {...attributes} {...listeners} aria-label="Drag to reorder" data-testid={`drag-handle-${id}`}>
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      {children}
    </TableRow>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const navigate = useNavigate();

  const visible = useMemo(() => {
    if (!products) return null;
    let list = products;
    const needle = search.trim().toLowerCase();
    if (needle) {
      list = list.filter((p) => p.name.toLowerCase().includes(needle) || (p.description || "").toLowerCase().includes(needle));
    }
    if (catFilter !== "all") {
      const childIds = categories.filter((c) => c.parent_id === catFilter).map((c) => c.id);
      list = list.filter((p) => p.category_id === catFilter || childIds.includes(p.category_id));
    }
    return list;
  }, [products, search, catFilter, categories]);

  const load = () => {
    API.get("/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
    API.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const patch = async (p, changes) => {
    try {
      await API.patch(`/admin/products/${p.id}`, { ...p, ...changes });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const adjustStock = async (p, delta) => {
    try {
      const r = await API.post(`/admin/products/${p.id}/stock`, { delta });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? r.data : x)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const duplicate = async (p) => {
    try {
      await API.post(`/admin/products/${p.id}/duplicate`);
      toast.success(`Duplicated "${p.name}" (created as inactive)`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (p) => {
    try {
      await API.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const visIds = visible.map((p) => p.id);
    const newVisIds = arrayMove(visIds, visIds.indexOf(active.id), visIds.indexOf(over.id));
    // splice the permuted visible items back into the full list at their collective positions
    const byId = Object.fromEntries(products.map((p) => [p.id, p]));
    const queue = newVisIds.map((pid) => byId[pid]);
    const visSet = new Set(visIds);
    const next = products.map((p) => (visSet.has(p.id) ? queue.shift() : p));
    setProducts(next);
    try {
      await API.post("/admin/products/reorder", { ordered_ids: next.map((p) => p.id) });
      toast.success("Order saved");
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl tracking-tight">Products</h1>
        <Button onClick={() => navigate("/admin/products/new")} className="rounded-full" data-testid="add-product-btn">
          <Plus className="mr-2 h-4 w-4" /> Add product
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products by name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-products-search"
          />
        </div>
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="h-9 rounded-md border bg-background px-3 text-sm w-full sm:w-56"
          data-testid="admin-products-category-filter"
        >
          <option value="all">All categories</option>
          {categories.filter((c) => !c.parent_id).map((p) => (
            <optgroup key={p.id} label={p.name}>
              <option value={p.id}>{p.name} (all)</option>
              {categories.filter((c) => c.parent_id === p.id).map((c) => (
                <option key={c.id} value={c.id}>— {c.name}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {!visible ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : visible.length === 0 ? (
        <p className="text-muted-foreground text-center py-16 border rounded-xl" data-testid="products-empty">
          {products.length === 0 ? "No products yet. Add your first product." : "No products match your search."}
        </p>
      ) : (
        <div className="border rounded-xl overflow-x-auto bg-card" data-testid="products-table">
          <p className="text-xs text-muted-foreground px-2 pt-3 pb-1" data-testid="products-count">
            Showing {visible.length} of {products.length} products · drag <GripVertical className="inline h-3 w-3 -mt-0.5" /> to reorder (shop order)
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={visible.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {visible.map((p) => (
                    <SortableRow key={p.id} id={p.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {p.images?.[0] ? (
                        <img src={imgUrl(p.images[0])} alt="" className="h-10 w-10 rounded-md object-contain bg-muted" />
                      ) : (
                        <div className="h-10 w-10 rounded-md bg-muted" />
                      )}
                      <div>
                        <p className="font-medium">{p.name}</p>
                        {(!p.in_stock || p.stock === 0) && <Badge variant="secondary" className="rounded-full text-[10px]">Out of stock</Badge>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{catName(p.category_id)}</TableCell>
                  <TableCell>
                    {p.sale_price ? (
                      <span><strong>{money(p.sale_price)}</strong> <span className="line-through text-muted-foreground text-xs">{money(p.price)}</span></span>
                    ) : (
                      money(p.price)
                    )}
                  </TableCell>
                  <TableCell data-testid={`stock-cell-${p.id}`}>
                    {p.stock === null || p.stock === undefined ? (
                      <span className="text-xs text-muted-foreground">Not tracked</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => adjustStock(p, -1)} disabled={p.stock <= 0} data-testid={`stock-decrease-${p.id}`} aria-label="Decrease stock"><Minus className="h-3.5 w-3.5" /></Button>
                        <span className={`w-8 text-center text-sm font-medium ${p.stock === 0 ? "text-destructive" : ""}`} data-testid={`stock-count-${p.id}`}>{p.stock}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => adjustStock(p, 1)} data-testid={`stock-increase-${p.id}`} aria-label="Increase stock"><Plus className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch checked={!!p.featured} onCheckedChange={(v) => patch(p, { featured: v })} data-testid={`featured-switch-${p.id}`} aria-label="Featured" />
                  </TableCell>
                  <TableCell>
                    <Switch checked={!!p.active} onCheckedChange={(v) => patch(p, { active: v })} data-testid={`active-switch-${p.id}`} aria-label="Active" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" asChild data-testid={`edit-product-${p.id}`} aria-label="Edit">
                        <Link to={`/admin/products/${p.id}`}><Pencil className="h-4 w-4" /></Link>
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => duplicate(p)} data-testid={`duplicate-product-${p.id}`} aria-label="Duplicate">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-product-${p.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{p.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>This cannot be undone. Consider disabling the product instead.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(p)} data-testid={`confirm-delete-product-${p.id}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                    </SortableRow>
                  ))}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </div>
      )}
    </div>
  );
}
